import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import ProposalAwareResumeItem from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/ExecutionEntryCompleted/ProposalAwareResumeItem'
import { isExecutionEntryProposalContent } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/proposalContents/proposalContent.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingEstablishmentChoiceEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/establishmentChoiceEntry.type'
import { MaintenanceDirectiveObjectiveData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { WrenchIcon } from 'lucide-react'
import { useMemo } from 'react'

interface ExecutionEntryObjectivePiecesProps {
  objective: MaintenanceDirectiveObjectiveData
  dataUpdatedAt?: number
}

const ExecutionEntryObjectivePieces = ({ objective, dataUpdatedAt }: ExecutionEntryObjectivePiecesProps) => {
  const { forwarding } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry,
    MaintenanceForwardingEstablishmentChoiceEntryKlass.ManualEntry
  >()

  const proposalStatus = forwarding?.establishment_choice_entry?.actual_proposal?.status
  const proposalContent = forwarding?.establishment_choice_entry?.actual_proposal?.proposal_content

  const pieceModifications = isExecutionEntryProposalContent(proposalContent, proposalStatus)
    ? proposalContent.piece_modifications
    : undefined

  const modificationByPieceId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof pieceModifications>[number]>()

    pieceModifications?.forEach((modification) => {
      map.set(modification.objective_piece_id, modification)
    })

    return map
  }, [pieceModifications])

  const piecesCountDisplay = useMemo(() => {
    const count = objective.execution_pieces?.length || 0

    if (count === 0) {
      return 'Nenhuma peça'
    }

    if (count === 1) {
      return '1 Peça'
    }

    return `${count} Peças`
  }, [dataUpdatedAt])

  const totalValueSum = useMemo(() => {
    return (
      objective.execution_pieces?.reduce((prev, curr) => {
        const modification = modificationByPieceId.get(curr.id)
        const value = modification?.new_total_value ?? curr.total_value
        return prev + Number(value)
      }, 0) || 0
    )
  }, [dataUpdatedAt, modificationByPieceId])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <p className="font-bold text-base">Peças</p>

          <div className="flex items-center gap-0.5 text-xs">
            <span>{piecesCountDisplay}</span>

            <span>•</span>

            <span className="text-valid">{formatCurrency(totalValueSum)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {objective.execution_pieces?.map((diagnosticPiece) => {
          const modification = modificationByPieceId.get(diagnosticPiece.id)

          return (
            <Box key={diagnosticPiece.id} className="p-4">
              <div className="flex gap-4">
                <ImageCarousel
                  files={diagnosticPiece.piece?.images?.map((image) => ({ id: image.url, file: image.url })) || []}
                  Placeholder={
                    <ImageWithPlaceholder
                      className="size-20 rounded-md"
                      iconClassName="size-10"
                      placeholderIcon={WrenchIcon}
                    />
                  }
                  className="size-20"
                  showExpandButton
                  showRemoveButton={false}
                />

                <div className="flex flex-col gap-2">
                  <p className="text-base font-bold">{diagnosticPiece?.piece?.display_name || '--'}</p>

                  <div className="flex items-center flex-wrap gap-3">
                    <ProposalAwareResumeItem
                      label="Valor unt."
                      originalValue={Number(diagnosticPiece?.unit_price) || 0}
                      newValue={modification?.new_unit_price != null ? Number(modification.new_unit_price) : null}
                      format={formatCurrency}
                    />

                    <p className="text-sm">x</p>

                    <ResumeItem className="text-sm" label="Qntd" value={formatUnit(diagnosticPiece.quantity, 'unit')} />

                    <p className="text-sm">=</p>

                    <ProposalAwareResumeItem
                      label="Valor total"
                      originalValue={Number(modification?.original_total_value ?? diagnosticPiece?.total_value) || 0}
                      newValue={modification?.new_total_value != null ? Number(modification.new_total_value) : null}
                      differencePercentage={modification?.difference_percentage}
                      format={formatCurrency}
                    />

                    <ProposalAwareResumeItem
                      label="Garantia"
                      originalValue={diagnosticPiece.warranty_in_days}
                      newValue={modification?.new_warranty_in_days}
                      format={(value) => `${value} Dias`}
                    />

                    <ResumeItem
                      className="text-sm"
                      label="Código da peça"
                      value={diagnosticPiece?.piece?.number || '--'}
                    />

                    <ResumeItem
                      className="text-sm"
                      label="Fabricante"
                      value={diagnosticPiece?.piece?.brand?.display_name || '--'}
                    />
                  </div>

                  <ResumeItem className="text-sm" label="Descrição" value={diagnosticPiece.description} />
                </div>
              </div>
            </Box>
          )
        })}
      </div>
    </div>
  )
}

export default ExecutionEntryObjectivePieces
